import { useState } from 'react';
import Button from '@mui/material/Button';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import AddIcon from '@mui/icons-material/Add';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ListDeclarationSummarySchema, ListValueSchema, type ListValueDto } from '@taqon/contracts';
import { useSearchParams } from 'react-router';
import { z } from 'zod';
import { getData, patchJson, postJson } from '../../shared/api/client';
import { hasCapability, useSession } from '../../shared/session/session';
import { FormDialog, StatusChip } from '../../shared/ui/forms';
import { EmptyState, ErrorState, LoadingState, PageHeader, Section } from '../../shared/ui/page';

const LISTS = '/api/v1/platform/lists';

/**
 * Configurable list values (SDD §15.3; ERD §8.7; FRD-CORE-042): the pick-lists modules declare —
 * client type, industry, lead source, activity type and so on — whose **values** are business data
 * maintained here. A value carries no behaviour; it is deactivated, never deleted, because records
 * keep referring to it. The list in view is kept in the address (`?list=`), so it survives a
 * refresh and can be linked to.
 */
export function ListValuesPage() {
  const [params, setParams] = useSearchParams();
  const selected = params.get('list');
  const [search, setSearch] = useState('');
  const lists = useQuery({
    queryKey: ['platform', 'lists'],
    queryFn: () => getData(LISTS, z.array(ListDeclarationSummarySchema)),
  });
  const term = search.trim().toLowerCase();
  const shown = (lists.data ?? []).filter(
    (list) =>
      term === '' ||
      list.listCode.toLowerCase().includes(term) ||
      list.description.toLowerCase().includes(term),
  );
  return (
    <Stack spacing={3}>
      <PageHeader
        title="List values"
        description="The choices offered in pick-lists across the system. Values are deactivated, never deleted."
      />
      {lists.isPending && <LoadingState label="Loading lists" />}
      {lists.isError && <ErrorState message={lists.error.message} />}
      {lists.data?.length === 0 && (
        <Section>
          <EmptyState title="No module declares a list yet" />
        </Section>
      )}
      {lists.data !== undefined && lists.data.length > 0 && (
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={3}
          sx={{ alignItems: 'flex-start' }}
        >
          <Section title="Lists">
            <TextField
              label="Search lists"
              size="small"
              fullWidth
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
              }}
            />
            {shown.length === 0 && <EmptyState title="No list matches the search" />}
            <List dense sx={{ minWidth: { md: 280 } }} aria-label="Declared lists">
              {shown.map((list) => (
                <ListItem key={list.listCode} disablePadding>
                  <ListItemButton
                    selected={list.listCode === selected}
                    onClick={() => {
                      setParams({ list: list.listCode });
                    }}
                  >
                    <ListItemText
                      primary={list.description}
                      secondary={`${list.listCode} · ${String(list.activeValues)} active`}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Section>
          <Stack sx={{ flex: 1, minWidth: 0, width: '100%' }}>
            {selected === null ? (
              <Section>
                <EmptyState title="Choose a list" detail="Its values appear here." />
              </Section>
            ) : (
              <ListValues listCode={selected} />
            )}
          </Stack>
        </Stack>
      )}
    </Stack>
  );
}

function ListValues({ listCode }: { listCode: string }) {
  const session = useSession();
  const canCreate = hasCapability(session.data, 'platform.configurable_list_value:create');
  const canChange = hasCapability(session.data, 'platform.configurable_list_value:change');
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<ListValueDto | null>(null);
  const values = useQuery({
    queryKey: ['platform', 'lists', listCode],
    queryFn: () => getData(`${LISTS}/${listCode}/values`, z.array(ListValueSchema)),
  });
  return (
    <Section
      title={listCode}
      actions={
        canCreate ? (
          <Button
            size="small"
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setAdding(true);
            }}
          >
            Add value
          </Button>
        ) : undefined
      }
    >
      {values.isPending && <LoadingState label="Loading values" />}
      {values.isError && <ErrorState message={values.error.message} />}
      {values.data?.length === 0 && (
        <EmptyState
          title="No values yet"
          detail="Records that need this list cannot be saved until it has an active value."
        />
      )}
      {values.data !== undefined && values.data.length > 0 && (
        <Table size="small" aria-label={`Values of ${listCode}`}>
          <TableHead>
            <TableRow>
              <TableCell>Label</TableCell>
              <TableCell>Code</TableCell>
              <TableCell>Order</TableCell>
              <TableCell>Status</TableCell>
              {canChange && <TableCell align="right">Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {values.data.map((value) => (
              <TableRow key={value.id} hover>
                <TableCell>{value.label}</TableCell>
                <TableCell>{value.valueCode}</TableCell>
                <TableCell>{value.sortOrder}</TableCell>
                <TableCell>
                  <StatusChip status={value.status} />
                </TableCell>
                {canChange && (
                  <TableCell align="right">
                    <Button
                      size="small"
                      onClick={() => {
                        setEditing(value);
                      }}
                    >
                      Edit
                    </Button>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      {adding && (
        <ValueDialog
          listCode={listCode}
          value={null}
          onClose={() => {
            setAdding(false);
          }}
        />
      )}
      {editing !== null && (
        <ValueDialog
          key={editing.id}
          listCode={listCode}
          value={editing}
          onClose={() => {
            setEditing(null);
          }}
        />
      )}
    </Section>
  );
}

function ValueDialog({
  listCode,
  value,
  onClose,
}: {
  listCode: string;
  value: ListValueDto | null;
  onClose: () => void;
}) {
  const session = useSession();
  const queryClient = useQueryClient();
  const [valueCode, setValueCode] = useState(value?.valueCode ?? '');
  const [label, setLabel] = useState(value?.label ?? '');
  const [sortOrder, setSortOrder] = useState(String(value?.sortOrder ?? 0));
  const [status, setStatus] = useState<'active' | 'inactive'>(value?.status ?? 'active');
  const csrf = session.data?.csrfToken ?? '';
  const save = useMutation({
    mutationFn: () => {
      if (value === null) {
        return postJson(
          `${LISTS}/${listCode}/values`,
          { valueCode: valueCode.trim(), label: label.trim(), sortOrder: Number(sortOrder) },
          ListValueSchema,
          csrf,
        );
      }
      const changes = {
        ...(label.trim() !== value.label ? { label: label.trim() } : {}),
        ...(Number(sortOrder) !== value.sortOrder ? { sortOrder: Number(sortOrder) } : {}),
        ...(status !== value.status ? { status } : {}),
      };
      return patchJson(
        `${LISTS}/${listCode}/values/${value.valueCode}`,
        changes,
        ListValueSchema,
        csrf,
        value.version,
      );
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['platform', 'lists'] });
      onClose();
    },
  });
  const unchanged =
    value !== null &&
    label.trim() === value.label &&
    Number(sortOrder) === value.sortOrder &&
    status === value.status;
  return (
    <FormDialog
      open
      title={value === null ? 'Add value' : `Edit ${value.label}`}
      submitLabel={value === null ? 'Add' : 'Save'}
      canSubmit={
        label.trim() !== '' &&
        /^\d+$/.test(sortOrder) &&
        (value !== null ? !unchanged : /^[a-z0-9][a-z0-9_]*$/.test(valueCode.trim()))
      }
      submitting={save.isPending}
      error={save.isError ? save.error.message : null}
      onClose={onClose}
      onSubmit={() => {
        save.mutate();
      }}
    >
      <TextField
        label="Code"
        required
        disabled={value !== null}
        value={valueCode}
        onChange={(e) => {
          setValueCode(e.target.value);
        }}
        helperText="Lower-case letters, digits and underscores. Fixed once created."
      />
      <TextField
        label="Label"
        required
        value={label}
        onChange={(e) => {
          setLabel(e.target.value);
        }}
      />
      <TextField
        label="Order"
        type="number"
        value={sortOrder}
        onChange={(e) => {
          setSortOrder(e.target.value);
        }}
      />
      {value !== null && (
        <TextField
          select
          label="Status"
          value={status}
          onChange={(e) => {
            setStatus(e.target.value === 'inactive' ? 'inactive' : 'active');
          }}
          helperText="An inactive value stays on existing records but cannot be chosen again."
        >
          <MenuItem value="active">Active</MenuItem>
          <MenuItem value="inactive">Inactive</MenuItem>
        </TextField>
      )}
    </FormDialog>
  );
}
